export const modeMeta = {
  registry: {
    title: "Supplier Machine Registry",
    searchPlaceholder: "Search supplier machine",
    emptyText: "No supplier-owned machines found for this branch yet.",
  },
  warranty: {
    title: "Supplier Warranty Tracking",
    searchPlaceholder: "Search warranty item",
    emptyText: "No supplier-owned machines with warranty details found.",
  },
  calibration: {
    title: "Supplier Calibration",
    searchPlaceholder: "Search calibration item",
    emptyText: "No supplier-owned calibration records found.",
  },
  stock: {
    title: "Supplier Machine Stock",
    searchPlaceholder: "Search stock item",
    emptyText: "No unassigned supplier stock found.",
  },
  tieup: {
    title: "Supplier Machine Tieup",
    searchPlaceholder: "Search tagged machine",
    emptyText: "No tagged supplier machines found.",
  },
};

const isTieupMachine = (item = {}) =>
  String(item?.category || "").toLowerCase() === "machines" &&
  String(item?.owwnersship || "tieup").toLowerCase() === "tieup" &&
  !item?.deletedAt;

export const formatDate = (value) => {
  if (!value) return "N/A";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString("en-PH", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

export const formatDateTime = (value) => {
  if (!value) return "N/A";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString("en-PH", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

export const formatCurrency = (value) => {
  const amount = Number(value || 0);

  return amount.toLocaleString("en-PH", {
    style: "currency",
    currency: "PHP",
    minimumFractionDigits: 2,
  });
};

export const getPmLabel = (pm = {}) => {
  if (!pm?.value || !pm?.unit) return "N/A";
  const suffix = Number(pm.value) > 1 ? "s" : "";
  return `${pm.value} ${pm.unit}${suffix}`;
};

export const getNextPmDate = (machine = {}) => {
  const { lastPMDate, pm = {} } = machine;
  if (!lastPMDate || !pm?.value || !pm?.unit) return null;

  const lastDate = new Date(lastPMDate);
  if (Number.isNaN(lastDate.getTime())) return null;

  const nextDate = new Date(lastDate);
  if (pm.unit === "day") nextDate.setDate(nextDate.getDate() + Number(pm.value));
  if (pm.unit === "month")
    nextDate.setMonth(nextDate.getMonth() + Number(pm.value));
  if (pm.unit === "year")
    nextDate.setFullYear(nextDate.getFullYear() + Number(pm.value));

  return nextDate;
};

export const getWarrantyStatus = (warranty) => {
  if (!warranty) return { label: "No warranty", tone: "secondary", daysLeft: null };

  const expiry = new Date(warranty);
  if (Number.isNaN(expiry.getTime())) {
    return { label: "Manual warranty", tone: "info", daysLeft: null };
  }

  const today = new Date();
  const diffMs = expiry.setHours(0, 0, 0, 0) - today.setHours(0, 0, 0, 0);
  const daysLeft = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (daysLeft < 0) return { label: "Expired", tone: "danger", daysLeft };
  if (daysLeft <= 30) return { label: "Expiring soon", tone: "warning", daysLeft };
  return { label: "Covered", tone: "success", daysLeft };
};

export const getCalibrationStatus = (machine = {}) => {
  const nextPmDate = getNextPmDate(machine);
  if (!nextPmDate) return { label: "No PM schedule", tone: "secondary" };

  const today = new Date();
  const diffMs = nextPmDate.setHours(0, 0, 0, 0) - today.setHours(0, 0, 0, 0);
  const daysLeft = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (daysLeft < 0) return { label: "Overdue", tone: "danger", daysLeft };
  if (daysLeft <= 14) return { label: "Due soon", tone: "warning", daysLeft };
  return { label: "On schedule", tone: "success", daysLeft };
};

export const buildMachineDataset = (collections = [], mode = "registry") => {
  const base = collections.filter(isTieupMachine);

  if (mode === "stock") {
    return base.filter((item) => !item?.branch);
  }

  if (mode === "tieup") {
    return base.filter((item) => item?.branch);
  }

  if (mode === "warranty") {
    return base.filter((item) => item?.warranty);
  }

  if (mode === "calibration") {
    return base.filter(
      (item) => item?.lisCapable || item?.lastPMDate || item?.pm?.value
    );
  }

  return base;
};

export const buildSummary = (machines = []) => {
  return {
    total: machines.length,
    functional: machines.filter((item) =>
      ["fully functional", "functional"].includes(
        String(item?.status || "").toLowerCase()
      )
    ).length,
    expiring: machines.filter(
      ({ warranty }) => getWarrantyStatus(warranty).tone === "warning"
    ).length,
    overdue: machines.filter(
      (item) => getCalibrationStatus(item).tone === "danger"
    ).length,
  };
};

const branchJourneyLabel = (branch = {}) => {
  const companyName = branch?.companyId?.name || "";
  const branchName = branch?.name || branch?.displayname || "";

  if (companyName && branchName) return `${companyName} -> ${branchName}`;
  return branchName || "Supplier stock";
};

export const snapshotActionLabel = (action = "") => {
  const labels = {
    registered: "Registered",
    tagged: "Tagged",
    untagged: "Untagged",
    transferred: "Transferred",
  };

  return labels[action] || "Updated";
};

export const formatSnapshotTrail = (snapshots = []) =>
  (Array.isArray(snapshots) ? snapshots : []).map((snapshot) => {
    const fromLabel = branchJourneyLabel(snapshot?.fromBranch);
    const toLabel = branchJourneyLabel(snapshot?.toBranch);
    let movement = toLabel;

    if (snapshot?.action === "untagged") {
      movement = `${fromLabel} -> Supplier stock`;
    } else if (snapshot?.action === "transferred") {
      movement = `${fromLabel} -> ${toLabel}`;
    } else if (snapshot?.action === "tagged") {
      movement = `Supplier stock -> ${toLabel}`;
    } else if (snapshot?.action === "registered") {
      movement = snapshot?.toBranch ? toLabel : "Supplier stock";
    }

    return {
      ...snapshot,
      actionLabel: snapshotActionLabel(snapshot?.action),
      movement,
    };
  });
